import { CSSProperties, FunctionComponent, memo, useContext, useEffect, useMemo } from 'react'
import { LuBolt, LuNavigation2 } from 'react-icons/lu'

import { MapMarker, MapShape } from '@/feature/map'
import { AssetMarker, getSpeedThresholdColor, ActiveAssetContext } from '@/feature/observatory'

import { Loader } from '@/lib/core/components/Loader'
import { SPEED_UNIT } from '@/lib/core/constants/constants'

import { ReplayContext } from '../../contexts/ReplayContext'
import { GeoFenceRoutePoint, MovementRoutePoint, ReplayProps, RoutePoint } from './Replay.types'
import { buildSpeedColoredSegments, formatEventTimestamp, Z_INDEX_MAP } from './Replay.utils'
import {
  useGeoFenceTitle,
  useReplay,
  useReplayMarkers,
  useReverseGeocodedAddress,
} from './useReplay'
import rescale from '@/utils/rescale'

import styles from './Replay.module.css'

const ICON_MAP = {
  MOVEMENT: LuNavigation2,
  ENTER_GEOFENCE: LuBolt,
  EXIT_GEOFENCE: LuBolt,
} as const

const MovementInfo = ({ point, showing }: { point: MovementRoutePoint; showing: boolean }) => {
  const address = useReverseGeocodedAddress(point.lat, point.lng, showing)

  return (
    <>
      <div className={styles.tooltipLineItem}>
        <span className={styles.label}>Time and date:</span>{' '}
        <span className={styles.value}>{formatEventTimestamp(point.when)}</span>
      </div>
      <div className={styles.tooltipLineItem}>
        <span className={styles.label}>Address:</span>{' '}
        <span className={styles.value}>{address ?? <Loader />}</span>
      </div>
      <div className={styles.tooltipLineItem}>
        <span className={styles.label}>Speed at location:</span>{' '}
        <span className={styles.value}>
          {point.speed == null ? '?' : `${Math.round(point.speed)} ${SPEED_UNIT}`}
        </span>
      </div>
    </>
  )
}

const GeoFenceEventInfo = ({
  point,
  showing,
  label,
}: {
  point: GeoFenceRoutePoint
  showing: boolean
  label: string
}) => {
  const title = useGeoFenceTitle(point.geofence.id, showing)

  return (
    <>
      <div className={styles.tooltipLineItem}>
        <span className={styles.label}>{label}:</span>{' '}
        <span className={styles.value}>{formatEventTimestamp(point.when)}</span>
      </div>
      <div className={styles.tooltipLineItem}>
        <span className={styles.label}>Geofence:</span>{' '}
        <span className={styles.value}>{title ?? <Loader />}</span>
      </div>
    </>
  )
}

const INFO_RENDER_MAP = {
  MOVEMENT: MovementInfo,
  ENTER_GEOFENCE: (props: { point: GeoFenceRoutePoint; showing: boolean }) => (
    <GeoFenceEventInfo {...props} label="Enter time" />
  ),
  EXIT_GEOFENCE: (props: { point: GeoFenceRoutePoint; showing: boolean }) => (
    <GeoFenceEventInfo {...props} label="Exit time" />
  ),
} as Record<RoutePoint['type'], FunctionComponent<{ point: RoutePoint; showing: boolean }>>

const EventMarker = ({
  point,
  showTooltip,
  setShowTooltip,
  zIndex,
  distanceForFade,
  isTerminator,
  zoom,
  minFadeDistance,
  fadeDistance,
  cluster,
}: {
  point: RoutePoint
  showTooltip: boolean
  setShowTooltip: (show: boolean) => void
  zIndex: number
  distanceForFade: number | false
  isTerminator: boolean
  zoom: number
  minFadeDistance: number
  fadeDistance: number
  cluster: boolean
}) => {
  const Icon = ICON_MAP[point.type]
  const InfoRender = INFO_RENDER_MAP[point.type]

  const { hoveredTimestamp } = useContext(ReplayContext)
  const isHovered = hoveredTimestamp === point.when.getTime()

  return (
    <MapMarker
      position={point}
      onClick={() => setShowTooltip(true)}
      zIndex={Math.round(zIndex)}
      className={[styles.marker, showTooltip || isHovered ? styles.markerActive : ''].join(' ')}
      clusterer={false}
      animate={false}
    >
      <div className={styles.markerTooltip}>
        <div className={styles.tooltipPosition}>
          <div className={styles.tooltipBody} data-testid="replay-marker-tooltip">
            {(showTooltip || isHovered) && <InfoRender point={point} showing={showTooltip} />}
          </div>
        </div>
      </div>
      <div
        className={styles.baseMarker}
        onClick={(e) => e.stopPropagation()}
        style={
          {
            '--z-index': zIndex,
            '--previous-distance':
              distanceForFade === false ? fadeDistance + minFadeDistance : distanceForFade,
            '--min-distance': minFadeDistance,
            '--fade-distance': fadeDistance,
            '--map-zoom': zoom,
            opacity: distanceForFade === false || !cluster ? 1 : undefined,
            color: point.type === 'MOVEMENT' ? getSpeedThresholdColor(point.speed) : undefined,
          } as CSSProperties
        }
        data-testid={`replay-marker-${point.type}`}
        data-type={point.type}
        data-terminator={isTerminator}
      >
        <Icon
          size={20}
          className={styles.baseMarkerIcon}
          style={
            {
              '--angle': `${point.heading ?? 0}deg`,
            } as CSSProperties
          }
        />
      </div>
    </MapMarker>
  )
}

const Markers = memo(function Markers({ route }: { route: RoutePoint[] }) {
  const { activeMarker, setActiveMarker, fadeDistances, zoom, minFadeDistance, fadeDistance, cluster } =
    useReplayMarkers(route)

  return route.map((point, index) => {
    const zRange = Z_INDEX_MAP[point.type]
    const isActive =
      activeMarker?.when.getTime() === point.when.getTime() && activeMarker?.type === point.type

    return (
      <EventMarker
        key={`${point.when.getTime()}${point.type}${index}`}
        point={point}
        distanceForFade={fadeDistances[index]}
        zIndex={rescale(route.length, zRange.max, index, 0, zRange.min)}
        showTooltip={isActive}
        setShowTooltip={(show) => setActiveMarker(show ? point : null)}
        isTerminator={index === 0 || index === route.length - 1}
        zoom={zoom}
        minFadeDistance={minFadeDistance}
        fadeDistance={fadeDistance}
        cluster={cluster}
      />
    )
  })
})

const SpeedColoredRoute = memo(function SpeedColoredRoute({ route }: { route: RoutePoint[] }) {
  const segments = useMemo(() => buildSpeedColoredSegments(route), [route])

  return (
    <>
      {/* White outline */}
      <MapShape
        shape={window.google.maps.Polyline}
        parameters={{
          geodesic: true,
          path: route.map((point) => ({ lat: point.lat, lng: point.lng })),
          strokeColor: '#FFF',
          strokeWeight: 15,
          zIndex: 1,
        }}
      />
      {/* Speed-colored segments with gradient interpolation */}
      {segments.map((segment, i) => (
        <MapShape
          key={i}
          shape={window.google.maps.Polyline}
          parameters={{
            geodesic: true,
            path: segment.path,
            strokeColor: segment.color,
            strokeWeight: 8,
            zIndex: 2,
          }}
        />
      ))}
    </>
  )
})

export const Replay = ({
  asset,
  startDate,
  endDate,
  stepSize = '1m',
  layers,
  history,
}: ReplayProps) => {
  useEffect(() => {
    if (layers?.length === 0) console.warn('No layers provided')
  }, [layers])

  const { route, setShownAsset, activeAssetState, replayValues } = useReplay(
    asset,
    startDate,
    endDate,
    stepSize,
    history,
  )

  return (
    <>
      <SpeedColoredRoute route={route} />
      <Markers route={route} />

      <ReplayContext.Provider value={replayValues}>
        <ActiveAssetContext.Provider value={activeAssetState}>
          <AssetMarker key={asset.id} asset={asset} onClick={() => setShownAsset(asset)} />
        </ActiveAssetContext.Provider>
      </ReplayContext.Provider>
    </>
  )
}
